import React, { useRef, useEffect, useCallback } from "react";
import * as d3 from "d3";
import {
  bindTooltipEvents,
  buildTooltipHtml,
  createTooltipHandlers,
  formatTooltipNumber,
  removeTooltip,
} from "../../common/components/d3/d3ChartShared";

const buildStackSegmentPath = (
  x,
  y,
  width,
  height,
  radius,
  roundTop,
  roundBottom,
) => {
  if (
    x === undefined ||
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    height <= 0 ||
    width <= 0
  ) {
    return "";
  }

  const topRadius = roundTop ? Math.min(radius, width / 2, height / 2) : 0;
  const bottomRadius = roundBottom ? Math.min(radius, width / 2, height / 2) : 0;
  const right = x + width;
  const bottom = y + height;

  return [
    `M${x},${bottom - bottomRadius}`,
    bottomRadius > 0
      ? `Q${x},${bottom} ${x + bottomRadius},${bottom}`
      : `L${x},${bottom}`,
    `L${right - bottomRadius},${bottom}`,
    bottomRadius > 0
      ? `Q${right},${bottom} ${right},${bottom - bottomRadius}`
      : `L${right},${bottom}`,
    `L${right},${y + topRadius}`,
    topRadius > 0
      ? `Q${right},${y} ${right - topRadius},${y}`
      : `L${right},${y}`,
    `L${x + topRadius},${y}`,
    topRadius > 0 ? `Q${x},${y} ${x},${y + topRadius}` : `L${x},${y}`,
    "Z",
  ].join(" ");
};

const LifecycleChart = React.memo(
  ({ data, title, uniqueStatusValues, colorScheme, vintages }) => {
    const chartRef = useRef(null);
    const tooltipRef = useRef(null);

    const renderChart = useCallback(() => {
      const innerChartRef = chartRef;
      if (!innerChartRef.current) {
        return;
      }

      const margin = { top: 56, right: 140, bottom: 50, left: 70 };
      const containerWidth = innerChartRef.current.offsetWidth;
      const containerHeight = 300;
      const width = containerWidth - margin.left - margin.right;
      const height = containerHeight - margin.top - margin.bottom;

      d3.select(innerChartRef.current).select("svg").remove();
      removeTooltip(tooltipRef);

      if (!width || width <= 0 || !height || height <= 0) {
        return;
      }

      const svg = d3
        .select(innerChartRef.current)
        .append("svg")
        .attr("width", "100%")
        .attr("height", "100%")
        .attr(
          "viewBox",
          `0 0 ${width + margin.left + margin.right} ${
            height + margin.top + margin.bottom
          }`,
        )
        .attr("preserveAspectRatio", "xMinYMin meet")
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      const dataMap = vintages.reduce((acc, vintage) => {
        acc[vintage] = { Vintage: vintage };
        uniqueStatusValues.forEach((status) => {
          acc[vintage][status] = 0;
        });
        return acc;
      }, {});

      data.forEach((item) => {
        if (dataMap[item.vintage]) {
          dataMap[item.vintage][item.status] += item.holdingQuantity;
        }
      });

      const processedData = Object.values(dataMap);

      const stack = d3
        .stack()
        .keys(uniqueStatusValues)
        .value((d, key) => d[key]);

      const stackedData = stack(processedData);

      const x = d3.scaleBand().domain(vintages).range([0, width]).padding(0.18);

      const y = d3
        .scaleLinear()
        .domain([0, d3.max(stackedData, (series) => d3.max(series, (d) => d[1])) || 0])
        .nice()
        .range([height, 0]);

      const color = d3
        .scaleOrdinal()
        .domain(uniqueStatusValues)
        .range(colorScheme);

      svg
        .append("g")
        .attr("class", "grid d3-chart-gridline")
        .call(d3.axisLeft(y).tickSize(-width).tickFormat(""));

      svg
        .append("g")
        .attr("transform", `translate(0,${height})`)
        .call(d3.axisBottom(x).tickSize(0))
        .attr("class", "d3-chart-axis")
        .selectAll("text")
        .attr("transform", "rotate(-45)")
        .style("text-anchor", "end");

      svg.append("g").call(d3.axisLeft(y)).attr("class", "d3-chart-axis");

      const { showTooltip, hideTooltip } = createTooltipHandlers({
        tooltipRef,
      });

      const stackCornerRadius = 3;
      const stackEdgesByVintage = new Map(
        vintages.map((vintage, vintageIndex) => {
          const activeSeries = stackedData.filter((series) => {
            const segment = series[vintageIndex];
            return segment && segment[1] - segment[0] > 0;
          });

          return [
            vintage,
            {
              bottomKey: activeSeries[0]?.key,
              topKey: activeSeries[activeSeries.length - 1]?.key,
            },
          ];
        }),
      );

      const stackGroups = svg
        .selectAll(".lifecycle-stack-group")
        .data(stackedData)
        .enter()
        .append("g")
        .attr("class", "lifecycle-stack-group");

      const stackSegments = stackGroups
        .selectAll("path")
        .data((series) =>
          series.map((segment) => ({
            key: series.key,
            vintage: segment.data.Vintage,
            start: segment[0],
            end: segment[1],
          })),
        )
        .enter()
        .append("path")
        .attr("class", "bar dashboard-bar dashboard-stacked-bar")
        .attr("fill", (d) => color(d.key))
        .attr("d", (d) => {
          const stackEdges = stackEdgesByVintage.get(d.vintage) || {};
          return buildStackSegmentPath(
            x(d.vintage),
            y(d.end),
            x.bandwidth(),
            y(d.start) - y(d.end),
            stackCornerRadius,
            d.key === stackEdges.topKey,
            d.key === stackEdges.bottomKey,
          );
        });

      bindTooltipEvents({
        selection: stackSegments,
        showTooltip,
        hideTooltip,
        htmlAccessor: (d) =>
          buildTooltipHtml({
            title: String(d.vintage),
            rows: [
              {
                label: d.key,
                value: formatTooltipNumber(d.end - d.start, {
                  maximumFractionDigits: 0,
                }),
                color: color(d.key),
              },
            ],
          }),
      });

      const legend = svg
        .append("g")
        .attr("transform", `translate(${width + 20}, -18)`)
        .selectAll(".legend")
        .data(uniqueStatusValues)
        .enter()
        .append("g")
        .attr("class", "legend")
        .attr("transform", (d, i) => `translate(0, ${i * 22})`);

      legend
        .append("rect")
        .attr("x", -18)
        .attr("width", 18)
        .attr("height", 18)
        .attr("rx", 4)
        .attr("ry", 4)
        .attr("class", "dashboard-pie-legend-swatch")
        .attr("fill", (d) => color(d));

      const wrapText = (text, textWidth) => {
        text.each(function () {
          const textElement = d3.select(this);
          const words = textElement.text().split(/\s+/).reverse();
          const lineHeight = 1.1;
          const xPosition = textElement.attr("x");
          const yPosition = textElement.attr("y");
          const dy = parseFloat(textElement.attr("dy")) - lineHeight / 2 || 0;
          let lineNumber = 0;
          let line = [];
          let word;
          let tspan = textElement
            .text(null)
            .append("tspan")
            .attr("x", xPosition)
            .attr("y", yPosition)
            .attr("dy", `${dy}em`);

          while ((word = words.pop())) {
            line.push(word);
            tspan.text(line.join(" "));
            if (tspan.node().getComputedTextLength() > textWidth) {
              line.pop();
              tspan.text(line.join(" "));
              line = [word];
              tspan = textElement
                .append("tspan")
                .attr("x", xPosition)
                .attr("y", yPosition)
                .attr("dy", `${++lineNumber * lineHeight + dy}em`)
                .text(word);
            }
          }
        });
      };

      legend
        .append("text")
        .attr("x", 6)
        .attr("y", 9)
        .attr("dy", ".35em")
        .style("text-anchor", "start")
        .attr("class", "d3-chart-legend-label")
        .style("font-size", "9px")
        .text((d) => d)
        .call(wrapText, 120);

      svg
        .append("text")
        .attr("x", width / 2)
        .attr("y", -margin.top / 2)
        .attr("text-anchor", "middle")
        .style("font-size", "16px")
        .attr("class", "d3-chart-title")
        .text(title);
    }, [data, vintages, title, uniqueStatusValues, colorScheme]);

    useEffect(() => {
      requestAnimationFrame(renderChart);

      return () => {
        removeTooltip(tooltipRef);
      };
    }, [renderChart]);

    useEffect(() => {
      const innerChartRef = chartRef;
      let resizeTimeout = null;

      const throttledResize = () => {
        if (!resizeTimeout) {
          resizeTimeout = setTimeout(() => {
            renderChart();
            resizeTimeout = null;
          }, 100);
        }
      };

      const observer = new ResizeObserver(throttledResize);

      if (innerChartRef.current) {
        observer.observe(innerChartRef.current);
      }

      return () => {
        if (innerChartRef.current) {
          observer.unobserve(innerChartRef.current);
        }
        if (resizeTimeout) {
          clearTimeout(resizeTimeout);
          resizeTimeout = null;
        }
      };
    }, [renderChart]);

    return (
      <div
        ref={chartRef}
        className="dashboard-d3-chart"
        style={{ width: "100%", height: "100%" }}
      ></div>
    );
  },
);

export default LifecycleChart;
